'use client';

// ClarificationQuestionItem - Individual question with answer input

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { useTranslations } from 'next-intl';
import type { ClarificationQuestionItemProps, ClarificationCategory } from './types';

// Category colors for visual distinction
const CATEGORY_COLORS: Record<ClarificationCategory, string> = {
  contact_info: '#2196F3', // Blue
  pricing: '#4CAF50', // Green
  schedule: '#FF9800', // Orange
  services: '#9C27B0', // Purple
  policies: '#795548', // Brown
  location: '#00BCD4', // Cyan
  credentials: '#3F51B5', // Indigo
  process: '#E91E63', // Pink
  other: '#607D8B', // Blue-grey
};

// Minimum and maximum answer lengths
const MIN_ANSWER_LENGTH = 1;
const MAX_ANSWER_LENGTH = 5000;

/**
 * ClarificationQuestionItem - Display a single clarification question with answer input
 *
 * Features:
 * - Question text prominently displayed
 * - Explanation of what's missing in muted text
 * - Category chip for context
 * - Expandable answer textarea
 * - Submit and Skip buttons
 * - Character count
 * - Status indicator (pending, answered, skipped)
 * - Edit button for answered questions
 * - Unskip button for skipped questions
 * - RTL support via CSS logical properties
 */
export function ClarificationQuestionItem({
  question,
  onSubmitAnswer,
  onSkip,
  onReopen,
  isSubmitting = false,
}: ClarificationQuestionItemProps) {
  const t = useTranslations('creator.clarification');
  const [answer, setAnswer] = useState('');
  const [isExpanded, setIsExpanded] = useState(question.status === 'pending');
  const [isEditing, setIsEditing] = useState(false);

  const isResolved = question.status !== 'pending';
  const categoryColor = CATEGORY_COLORS[question.category];
  const characterCount = answer.length;
  const isValidLength =
    characterCount >= MIN_ANSWER_LENGTH && characterCount <= MAX_ANSWER_LENGTH;

  const handleSubmit = () => {
    if (isValidLength && !isSubmitting) {
      onSubmitAnswer(question.id, answer);
      setIsEditing(false);
    }
  };

  const handleSkip = () => {
    if (!isSubmitting) {
      onSkip(question.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Pre-fill with existing answer
    setAnswer(question.answer || '');
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleUnskip = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReopen) {
      onReopen(question.id);
      setIsExpanded(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setAnswer('');
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (question.status) {
      case 'answered':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'skipped':
        return <SkipNextIcon sx={{ color: 'text.disabled', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        opacity: isResolved ? 0.7 : 1,
        borderColor: isResolved ? 'divider' : 'primary.light',
        transition: 'opacity 0.2s, border-color 0.2s',
      }}
    >
      <CardContent sx={{ pb: isExpanded ? 2 : 1, '&:last-child': { pb: isExpanded ? 2 : 1 } }}>
        {/* Question header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            cursor: isResolved ? 'pointer' : 'default',
          }}
          onClick={isResolved ? () => setIsExpanded(!isExpanded) : undefined}
        >
          {/* Category chip */}
          <Chip
            label={t(`categories.${question.category}`)}
            size="small"
            sx={{
              backgroundColor: categoryColor,
              color: 'white',
              fontWeight: 500,
              fontSize: '0.7rem',
              height: 22,
              flexShrink: 0,
            }}
          />

          {/* Question text */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              fontWeight="medium"
              sx={{
                color: isResolved ? 'text.secondary' : 'text.primary',
                textDecoration: isResolved ? 'line-through' : 'none',
              }}
            >
              {question.question}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                fontStyle: question.status === 'answered' ? 'normal' : 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {question.status === 'answered' && question.answer
                ? question.answer
                : question.explanation}
            </Typography>
          </Box>

          {/* Status indicator */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            {isResolved && (
              isExpanded ? (
                <ExpandLessIcon sx={{ color: 'text.secondary' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
              )
            )}
          </Box>
        </Box>

        {/* Answer section (expandable) - show for pending or when editing */}
        <Collapse in={isExpanded && (!isResolved || isEditing)}>
          <Box sx={{ mt: 2 }}>
            {/* Answer input */}
            <TextField
              fullWidth
              multiline
              rows={2}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t('question.answerPlaceholder')}
              disabled={isSubmitting}
              error={characterCount > MAX_ANSWER_LENGTH}
              sx={{ mb: 1 }}
            />

            {/* Character count and actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('question.characterCount', { count: characterCount })}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Cancel button (when editing) */}
                {isEditing && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    {t('question.cancel')}
                  </Button>
                )}

                {/* Skip button (only for pending, not editing) */}
                {!isEditing && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSkip}
                    disabled={isSubmitting}
                    startIcon={<SkipNextIcon />}
                  >
                    {t('question.skip')}
                  </Button>
                )}

                {/* Submit button */}
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit}
                  disabled={!isValidLength || isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                >
                  {isEditing ? t('question.updateAnswer') : t('question.submitAnswer')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>

        {/* Show answer if resolved and not editing */}
        <Collapse in={isExpanded && isResolved && question.status === 'answered' && !isEditing}>
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: 'action.hover',
              borderRadius: 1,
              borderInlineStart: '3px solid',
              borderInlineStartColor: 'success.main',
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {question.answer}
            </Typography>
          </Box>

          {/* Edit button for answered questions */}
          {onReopen && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                size="small"
                onClick={handleEdit}
                startIcon={<EditIcon />}
                sx={{ color: 'text.secondary' }}
              >
                {t('question.edit')}
              </Button>
            </Box>
          )}
        </Collapse>

        {/* Show skipped status with unskip button */}
        <Collapse in={isExpanded && isResolved && question.status === 'skipped'}>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              {t('question.skipped')}
            </Typography>

            {/* Unskip button */}
            {onReopen && (
              <Button
                variant="text"
                size="small"
                onClick={handleUnskip}
                startIcon={<UndoIcon />}
                sx={{ color: 'text.secondary' }}
              >
                {t('question.unskip')}
              </Button>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default ClarificationQuestionItem;
